<template>
  <OverlayProgress :loading="loading" :error="''">
    <BasicCard :title="la('title')">
      <v-row>
        <v-col cols="12">
          <span class="text-caption">{{ lv.label_src_balance }}</span>
          <v-text-field
            v-model.number="balanceValue"
            v-mask:currency
            :prefix="walletData?.currency_id"
            required
            readonly
          />
        </v-col>
        <v-col cols="12" class="d-flex align-center justify-center pa-0">
          <v-btn color="primary" @click.stop="switchDirection">
            <VIcon :icon="directionIcon" />
          </v-btn>
        </v-col>
        <v-col cols="12">
          <span class="text-caption">{{ lv.label_dst_balance }}</span>
          <v-text-field v-model.number="youzanBalance" required readonly />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <span class="text-caption">{{ lv.label_debit_amount }}</span>
          <v-text-field
            ref="amountToSendInput"
            v-model.number="amountToSend"
            v-mask:currency
            :prefix="walletData?.currency_id"
            required
            :rules="[amountSrcWalletRules]"
          />
        </v-col>
      </v-row>

      <v-expand-transition>
        <v-alert
          v-if="errorMessage"
          class="mb-4 mt-2"
          type="error"
          closable
          @click:close="errorMessageReset"
        >
          {{ errorMessage }}
        </v-alert>
      </v-expand-transition>
      <div class="d-flex flex-row justify-center">
        <v-btn
          class="ma-1"
          color="primary"
          type="submit"
          :disabled="!isValid"
          @click.prevent="handleTransfer(transferDirection === TRANSFER_DIRECTIONS.TO_YOUZAN
            ? transferFromWallet : transferToWallet)"
        >
          {{ transferButtonText }}
        </v-btn>
        <v-btn class="ma-1" color="secondary" @click.stop="reset">
          {{ lv.button_reset }}
        </v-btn>
      </div>
    </BasicCard>
  </OverlayProgress>
</template>

<script setup lang="ts">
import {
  useAwaitAccountId,
  useAwaitValue,
  useLocalization,
  usePointsTransfer,
  useToast,
  useWallet,
} from '@/composables';
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import OverlayProgress from '@/components/ui/base/OverlayProgress.vue';
import BasicCard from '@/components/ui/base/BasicCard.vue';
import { apiErrorToString } from '@ui-api3-sdk/api/api3';
import { PointsTransferPayload } from '@/composables/usePointsTransfer.ts';

const TRANSFER_DIRECTIONS = {
  TO_YOUZAN: 'to_youzan',
  TO_WALLET: 'to_wallet',
};

const { lt, lv, la } = useLocalization('uiWalletToYouzan', [
  'title',
  'label_src_balance',
  'label_dst_balance',

  'label_debit_amount',
  'error_not_number',
  'error_amount_required',

  'transfer_success',
  'button_transfer_to_youzan',
  'button_transfer_from_youzan',

  'button_reset',
]);

const { transferFromWallet, transferToWallet, getBalance } = usePointsTransfer();

const amountToSendInput = ref<HTMLFormElement | null>(null);
const loading = ref(true);
const amountToSend = ref(0);
const youzanBalance = ref<number>(0);
const transferDirection = ref<string>(TRANSFER_DIRECTIONS.TO_YOUZAN);
const errorMessage = ref('');
const accountId = ref<number | null>(null);
const walletData = ref<Record<string, any> | null>(null);

const props = withDefaults(
  defineProps<{
    id?: string;
    walletId?: number;
  }>(),
  {
    id: '',
    walletId: 108,
  },
);

const balanceValue = computed(() =>
  walletData.value ? `${walletData.value.balance} ${walletData.value.currency_id}` : 0,
);
const directionIcon = computed(() =>
  transferDirection.value === TRANSFER_DIRECTIONS.TO_YOUZAN
    ? 'mdi-arrow-down'
    : 'mdi-arrow-up',
);
const transferButtonText = computed(() =>
  transferDirection.value === TRANSFER_DIRECTIONS.TO_YOUZAN
    ? lv.value.button_transfer_to_youzan
    : lv.value.button_transfer_from_youzan,
);
const isValid = computed(
  () => accountId.value && walletData.value && amountToSend.value > 0,
);

watch(amountToSend, (value, oldValue) => {
  if (value === oldValue) return;

  switch (transferDirection.value) {
    case TRANSFER_DIRECTIONS.TO_YOUZAN:
      if (value > walletData.value?.balance)
        amountToSend.value = walletData.value?.balance || 0;
      break;
    case TRANSFER_DIRECTIONS.TO_WALLET:
      if (value > youzanBalance.value) amountToSend.value = youzanBalance.value || 0;
      break;
  }
});

const amountSrcWalletRules = (v: number) => {
  if (!v) return lt('error_amount_required');

  if (isNaN(v) || !isFinite(v)) return lt('error_not_number');
  if (v < 0.01) return lt('error_amount_required');

  return true;
};

function reset() {
  transferDirection.value = TRANSFER_DIRECTIONS.TO_YOUZAN;
  amountToSend.value = 0;
  nextTick(() => {
    amountToSendInput.value?.resetValidation();
  });
}

function switchDirection() {
  transferDirection.value =
    transferDirection.value === TRANSFER_DIRECTIONS.TO_YOUZAN
      ? TRANSFER_DIRECTIONS.TO_WALLET
      : TRANSFER_DIRECTIONS.TO_YOUZAN;

  amountToSend.value = 0;
}

async function fetchWalletData() {
  try {
    loading.value = true;
    accountId.value = await useAwaitAccountId();
    walletData.value = await useAwaitValue(
      useWallet(accountId.value, props.walletId),
      'currentWallet',
    );
  } catch (e) {
    errorMessage.value = apiErrorToString(e);
  } finally {
    loading.value = false;
  }
}

async function handleTransfer(fn: (payload: PointsTransferPayload) => Promise<any>) {
  if (!accountId.value || !walletData.value || amountToSend.value === 0) return;

  try {
    loading.value = true;
    const payload: PointsTransferPayload = {
      accountId: accountId.value,
      walletTypeId: props.walletId,
      amount: amountToSend.value,
    };
    await fn(payload);
    await updateBalances();

    useToast().ok(lt('transfer_success'));
    reset();
    errorMessageReset();
  } catch (e) {
    errorMessage.value = apiErrorToString(e);
  } finally {
    loading.value = false;
  }
}

async function updateYozanBalance() {
  try {
    loading.value = true;
    if (!accountId.value) return;
    youzanBalance.value =
      ((await getBalance(accountId.value))?.payload?.balance as number) ?? null;
  } catch (error) {
    errorMessage.value = apiErrorToString(error);
  } finally {
    loading.value = false;
  }
}

async function updateBalances() {
  await fetchWalletData();
  await updateYozanBalance();
}

function errorMessageReset() {
  errorMessage.value = '';
}

onMounted(async () => {
  await updateBalances();
});
</script>
